import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';

// Função para calcular a idade automaticamente
function calculateAge(birthDate) {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  // Se ainda não fez aniversário este ano, subtrai 1
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
}

export default function AboutMe() {
  const { t } = useTranslation();
  
  // Data de nascimento: 04 de outubro de 1994
  const birthDate = '1994-10-04';
  const age = calculateAge(birthDate);
  
  const infoItems = [
    {
      icon: 'bi-person',
      titleKey: 'name',
      content: 'Daniel Marnet',
      color: '#00f5ff'
    },
    {
      icon: 'bi-calendar-event',
      titleKey: 'age',
      content: `${age} ${t('aboutMe.basicInfo.ageContent')}`,
      color: '#00ff88'
    },
    {
      icon: 'bi-geo-alt',
      titleKey: 'city',
      content: 'São José dos Campos – SP',
      color: '#ff6b6b'
    }
  ];

  // Ícones para educação
  // Mestrado, Graduação, Bacharelado (Eng Mecânica - engrenagem), Pós-graduação, Licenciatura, Mestrado (Interrompido)
  const educationIcons = ['bi-airplane-engines', 'bi-code-slash', 'bi-gear', 'bi-award', 'bi-book', 'bi-graph-up'];
  const educationItems = t('aboutMe.educationItems').map((item, index) => ({
    ...item,
    icon: educationIcons[index],
    color: ['#00d4ff', '#ffa500', '#0077b5', '#00ff88', '#ff69b4', '#9b59b6'][index]
  }));

  // Ícones para atividades
  const activityIcons = ['bi-car-front', 'bi-building', 'bi-person-workspace'];
  const activityColors = ['#ff69b4', '#00ff88', '#ffa500'];
  const activities = t('aboutMe.activities').map((item, index) => ({
    ...item,
    icon: activityIcons[index],
    color: activityColors[index]
  }));

  // Ícones para experiências
  // Software Developer, Analista, Pesquisador de Mestrado, Pesquisador CNPq, Gerente de Projetos, Estagiário
  const experienceIcons = ['bi-wind', 'bi-cloud', 'bi-eye', 'bi-robot', 'bi-sun', 'bi-lightning-charge'];
  const experienceColors = ['#00d4ff', '#00f5ff', '#ff6b6b', '#ff69b4', '#ffa500', '#00ff88'];
  const experiences = t('aboutMe.experiences').map((item, index) => ({
    ...item,
    icon: experienceIcons[index],
    color: experienceColors[index]
  }));

  const technicalSkills = t('aboutMe.technicalSkills.categories');
  const softSkills = t('aboutMe.softSkills.skills');

  return (
    <section id="about-me" className="panel">
      <div className="d-flex align-items-center mb-4">
        <div className="me-3" style={{ fontSize: '2.5rem' }}>
          <i className="bi bi-person-badge" style={{ color: 'var(--accent)' }}></i>
        </div>
        <div>
          <h2 className="mb-2">{t('aboutMe.title')}</h2>
          <p 
            className="mb-0" 
            style={{ 
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.85)',
              fontFamily: 'var(--font-body)',
              fontWeight: '400',
              lineHeight: '1.7',
              letterSpacing: '0.4px',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
            }}
          >
            {t('aboutMe.description')}
          </p>
        </div>
      </div>

      {/* Informações Básicas */}
      <Row className="g-3 mb-4">
        {infoItems.map((item, index) => (
          <Col key={index} xs={12} sm={6} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.05, y: -5 }}
            >
              <Card
                style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: `1px solid ${item.color}40`,
                  borderRadius: '0.75rem',
                  transition: 'all 0.3s'
                }}
              >
                <Card.Body className="text-center p-3">
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem', color: item.color }}>
                    <i className={`bi ${item.icon}`}></i>
                  </div>
                  <Card.Title
                    style={{
                      fontSize: '0.9rem',
                      color: item.color,
                      fontWeight: '600',
                      marginBottom: '0.5rem'
                    }}
                  >
                    {item.titleKey ? t(`aboutMe.basicInfo.${item.titleKey}`) : item.title}
                  </Card.Title>
                  <Card.Text
                    style={{
                      fontSize: '0.95rem',
                      color: 'var(--text-light)',
                      margin: 0
                    }}
                  >
                    {item.content}
                  </Card.Text>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        ))}
      </Row>

      {/* Experiências Profissionais */}
      <div className="mb-4">
        <h3 style={{ color: 'var(--accent)', fontSize: '1.5rem', marginBottom: '1.5rem' }}>
          <i className="bi bi-briefcase me-2"></i>{t('aboutMe.experiencesTitle')}
        </h3>
        <Row className="g-3">
          {experiences.map((item, index) => (
            <Col key={index} xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.05, y: -5 }}
                style={{ height: '100%' }}
              >
                <Card
                  className="h-100"
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: `2px solid ${item.color}40`,
                    borderRadius: '1rem',
                    height: '100%',
                    transition: 'all 0.3s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Card.Body className="p-4" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div className="d-flex align-items-start mb-3">
                      <div style={{ fontSize: '2rem', marginRight: '1rem', color: item.color }}>
                        <i className={`bi ${item.icon}`}></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <Card.Title
                          style={{
                            fontSize: '1.1rem',
                            color: item.color,
                            fontWeight: '600',
                            marginBottom: '0.25rem'
                          }}
                        >
                          {item.title}
                        </Card.Title>
                        <Card.Text
                          style={{
                            fontSize: '0.95rem',
                            color: 'var(--text-light)',
                            fontWeight: '500',
                            marginBottom: '0.25rem'
                          }}
                        >
                          {item.company}
                        </Card.Text>
                        <Card.Text
                          style={{
                            fontSize: '0.85rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginBottom: '0.75rem',
                            fontStyle: 'italic'
                          }}
                        >
                          {item.period}
                        </Card.Text>
                        <Card.Text
                          style={{
                            fontSize: '0.9rem',
                            color: 'rgba(255, 255, 255, 0.8)',
                            margin: 0,
                            lineHeight: '1.5'
                          }}
                        >
                          {item.description}
                        </Card.Text>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Formação Acadêmica */}
      <div className="mb-4">
        <h3 style={{ color: 'var(--accent)', fontSize: '1.5rem', marginBottom: '1.5rem' }}>
          <i className="bi bi-mortarboard me-2"></i>{t('aboutMe.education.title')}
        </h3>
        <Row className="g-3">
          {educationItems.map((item, index) => (
            <Col key={index} xs={12} md={6} lg={4}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.03, x: 5 }}
                style={{ height: '100%' }}
              >
                <Card
                  className="h-100"
                  style={{
                    background: `linear-gradient(135deg, ${item.color}15, ${item.color}05)`,
                    border: `2px solid ${item.color}40`,
                    borderRadius: '1rem',
                    height: '100%',
                    transition: 'all 0.3s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Card.Body className="p-4" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div
                      style={{
                        fontSize: '2.5rem',
                        marginBottom: '1rem',
                        filter: `drop-shadow(0 0 8px ${item.color}80)`,
                        color: item.color
                      }}
                    >
                      <i className={`bi ${item.icon}`}></i>
                    </div>
                    <Card.Title
                      style={{
                        fontSize: '1rem',
                        color: item.color,
                        fontWeight: '600',
                        marginBottom: '0.5rem'
                      }}
                    >
                      {item.title}
                    </Card.Title>
                    <Card.Text
                      style={{
                        fontSize: '1.1rem',
                        color: 'var(--text-light)',
                        fontWeight: '500',
                        marginBottom: '0.25rem'
                      }}
                    >
                      {item.content}
                    </Card.Text>
                    <Card.Text
                      style={{
                        fontSize: '0.85rem',
                        color: 'rgba(255, 255, 255, 0.7)',
                        marginBottom: '0.5rem',
                        fontStyle: 'italic'
                      }}
                    >
                      {item.institution}
                    </Card.Text>
                    {item.description && (
                      <Card.Text
                        style={{
                          fontSize: '0.8rem',
                          color: 'rgba(255, 255, 255, 0.6)',
                          margin: 0,
                          fontStyle: 'italic'
                        }}
                      >
                        {item.description}
                      </Card.Text>
                    )}
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </div>

      

      {/* Atividades Extracurriculares */}
      <div className="mb-4">
        <h3 style={{ color: 'var(--accent)', fontSize: '1.5rem', marginBottom: '1.5rem' }}>
          <i className="bi bi-star me-2"></i>{t('aboutMe.activitiesTitle')}
        </h3>
        <Row className="g-3">
          {activities.map((item, index) => (
            <Col key={index} xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.05, y: -5 }}
                style={{ height: '100%' }}
              >
                <Card
                  className="h-100"
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: `1px solid ${item.color}40`,
                    borderRadius: '0.75rem',
                    height: '100%',
                    transition: 'all 0.3s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Card.Body className="p-3" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div className="d-flex align-items-start">
                      <div style={{ fontSize: '2rem', marginRight: '1rem', color: item.color }}>
                        <i className={`bi ${item.icon}`}></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <Card.Title
                          style={{
                            fontSize: '1rem',
                            color: item.color,
                            fontWeight: '600',
                            marginBottom: '0.25rem'
                          }}
                        >
                          {item.title}
                        </Card.Title>
                        <Card.Text
                          style={{
                            fontSize: '0.95rem',
                            color: 'var(--text-light)',
                            fontWeight: '500',
                            marginBottom: '0.5rem'
                          }}
                        >
                          {item.content}
                        </Card.Text>
                        {item.description && (
                          <Card.Text
                            style={{
                              fontSize: '0.85rem',
                              color: 'rgba(255, 255, 255, 0.7)',
                              margin: 0,
                              fontStyle: 'italic',
                              lineHeight: '1.4'
                            }}
                          >
                            {item.description}
                          </Card.Text>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Competências Técnicas */}
      <div className="mb-4">
        <h3 style={{ color: 'var(--accent)', fontSize: '1.5rem', marginBottom: '1.5rem' }}>
          <i className="bi bi-code-slash me-2"></i>{t('aboutMe.technicalSkillsTitle')}
        </h3>
        <Row className="g-3">
          {technicalSkills.map((category, index) => (
            <Col key={index} xs={12} sm={6} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
              >
                <Card
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(0, 245, 255, 0.3)',
                    borderRadius: '1rem',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Card.Body 
                    className="p-3"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1
                    }}
                  >
                    <Card.Title
                      style={{
                        fontSize: '1rem',
                        color: 'var(--accent)',
                        fontWeight: '600',
                        marginBottom: '0.75rem',
                        flexShrink: 0
                      }}
                    >
                      {category.category}
                    </Card.Title>
                    <div 
                      className="d-flex flex-wrap gap-2"
                      style={{
                        flex: 1,
                        alignContent: 'flex-start'
                      }}
                    >
                      {category.skills.map((skill, skillIndex) => (
                        <motion.span
                          key={skillIndex}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.9 + index * 0.1 + skillIndex * 0.05, duration: 0.3 }}
                          whileHover={{ scale: 1.1 }}
                          style={{
                            display: 'inline-block',
                            padding: '0.4rem 0.8rem',
                            background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.2), rgba(0, 200, 255, 0.1))',
                            border: '1px solid rgba(0, 245, 255, 0.4)',
                            borderRadius: '1.5rem',
                            fontSize: '0.85rem',
                            color: 'var(--text-light)',
                            fontWeight: '500',
                            marginBottom: '0.5rem'
                          }}
                        >
                          {skill}
                        </motion.span>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Soft Skills */}
      <div>
        <h3 style={{ color: 'var(--accent)', fontSize: '1.5rem', marginBottom: '1.5rem' }}>
          <i className="bi bi-trophy me-2"></i>{t('aboutMe.softSkills.title')}
        </h3>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          <Card
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(0, 245, 255, 0.3)',
              borderRadius: '1rem'
            }}
          >
            <Card.Body className="p-4">
              <div className="d-flex flex-wrap gap-2">
                {softSkills.map((skill, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.3 + index * 0.05, duration: 0.3 }}
                    whileHover={{ scale: 1.1 }}
                    style={{
                      display: 'inline-block',
                      padding: '0.5rem 1rem',
                      background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.2), rgba(0, 200, 255, 0.1))',
                      border: '1px solid rgba(0, 245, 255, 0.4)',
                      borderRadius: '2rem',
                      fontSize: '0.9rem',
                      color: 'var(--text-light)',
                      fontWeight: '500',
                      marginBottom: '0.5rem'
                    }}
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}
